import { ScrollText } from "lucide-react";
import { MarketingHeader } from "@/components/marketing-header";
import { Footer } from "@/components/footer";
import { useSeo, SEO_PAGES } from "@/hooks/use-seo";

const LAST_UPDATED = "April 18, 2026";

const SECTIONS = [
  {
    title: "1. Agreement to these Terms",
    body: (
      <p>
        Welcome to UnfoldWin. These Terms of Service ("Terms") govern your access
        to and use of UnfoldWin's website, products, and services (collectively,
        the "Service"). By creating an account or using the Service you agree to
        these Terms.
      </p>
    ),
  },
  {
    title: "2. Eligibility",
    body: (
      <p>
        You must be at least 13 years old (or the minimum age of digital consent
        in your jurisdiction) to use UnfoldWin. By using the Service, you
        represent that you meet this requirement and that you have the authority
        to accept these Terms.
      </p>
    ),
  },
  {
    title: "3. Your account",
    body: (
      <>
        <p>
          You're responsible for keeping your login credentials secure and for all
          activity under your account. Notify us promptly if you believe your
          account has been accessed without your permission.
        </p>
        <p>
          You agree to provide accurate information when signing up and to keep
          it current.
        </p>
      </>
    ),
  },
  {
    title: "4. Your content",
    body: (
      <>
        <p>
          You keep ownership of the journal entries, reflections, voice notes,
          and other content you create on UnfoldWin ("Your Content").
        </p>
        <p>
          You grant UnfoldWin a limited, worldwide, non-exclusive licence to
          host, store, process, and display Your Content solely for the purpose
          of operating and improving the Service for you — including sending it
          to third-party AI providers to generate reflections and journal
          entries.
        </p>
      </>
    ),
  },
  {
    title: "5. Acceptable use",
    body: (
      <>
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Use the Service for anything unlawful, harmful, or abusive.</li>
          <li>Upload content that infringes others' rights or violates privacy.</li>
          <li>Attempt to access accounts, systems, or data that don't belong to you.</li>
          <li>Interfere with, disrupt, or reverse-engineer the Service.</li>
          <li>Use automated means to scrape, copy, or overload the Service.</li>
        </ul>
      </>
    ),
  },
  {
    title: "6. AI-generated output",
    body: (
      <p>
        UnfoldWin uses third-party AI models to generate reflections and journal
        entries based on the content you provide. AI output may be inaccurate,
        incomplete, or unexpected. UnfoldWin is not a substitute for professional
        medical, mental-health, financial, or legal advice. Use your own judgement.
      </p>
    ),
  },
  {
    title: "7. Subscriptions and payments",
    body: (
      <p>
        Some features require a paid subscription. Fees, billing cycles, and
        renewal terms are shown at checkout. Subscriptions renew automatically
        until cancelled. You can cancel at any time from your account settings;
        cancellation takes effect at the end of the current billing period. We
        don't offer refunds for partial periods except where required by law.
      </p>
    ),
  },
  {
    title: "8. Termination",
    body: (
      <p>
        You can stop using UnfoldWin and delete your account at any time. We may
        suspend or terminate your access if you violate these Terms or if we
        need to protect the Service or its users. On termination, your right to
        use the Service ends immediately.
      </p>
    ),
  },
  {
    title: "9. Disclaimers",
    body: (
      <p>
        The Service is provided "as is" and "as available" without warranties of
        any kind, express or implied. We don't guarantee that the Service will
        be uninterrupted, error-free, or suited to your particular needs.
      </p>
    ),
  },
  {
    title: "10. Limitation of liability",
    body: (
      <p>
        To the maximum extent permitted by law, UnfoldWin will not be liable for
        any indirect, incidental, special, consequential, or punitive damages,
        or any loss of profits, data, or goodwill, arising from your use of the
        Service.
      </p>
    ),
  },
  {
    title: "11. Changes to the Service and Terms",
    body: (
      <p>
        We're always improving UnfoldWin and may change features from time to
        time. We may also update these Terms. If changes are significant, we'll
        let you know in-product or by email. Continued use after changes means
        you accept the updated Terms.
      </p>
    ),
  },
  {
    title: "12. Governing law",
    body: (
      <p>
        These Terms are governed by the laws of the jurisdiction where UnfoldWin
        is established, without regard to conflict-of-law rules. Disputes will
        be resolved in the competent courts of that jurisdiction.
      </p>
    ),
  },
  {
    title: "13. Contact",
    body: (
      <p>
        Questions about these Terms? Email us at{" "}
        <a
          href="mailto:hello@unfoldwin.com"
          className="text-primary underline underline-offset-2"
        >
          hello@unfoldwin.com
        </a>
        .
      </p>
    ),
  },
];

export default function TermsPage() {
  useSeo(SEO_PAGES["/terms"]);
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <MarketingHeader />

      <main className="flex-1">
        <section className="py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-5">
            <div className="mb-10">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest mb-5">
                <ScrollText className="w-3.5 h-3.5" />
                Legal
              </span>
              <h1 className="font-display text-4xl md:text-5xl font-semibold text-foreground mb-3">
                Terms of Service
              </h1>
              <p className="text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>
            </div>

            <div className="space-y-8 text-[15px] leading-relaxed text-muted-foreground">
              {SECTIONS.map((section) => (
                <section key={section.title}>
                  <h2 className="font-display text-xl font-medium text-foreground mb-3">
                    {section.title}
                  </h2>
                  <div className="space-y-3">{section.body}</div>
                </section>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
